import type { ICellRendererParams, ITooltipParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { differenceInMilliseconds } from 'date-fns';
import { get } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { formattedDate, formattedUTCDate } from '../../../utils';
import { Text } from '../../Text';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';
import { dateStringComparator } from './utils';

export type ExpiryCountdownParams<TData = unknown> = {
  /**
   * Optional callback to override expiry detection.
   * When it returns true, the cell shows the expired state regardless of the expiry date.
   * Useful for cases where an entity becomes terminal before its TTL runs out.
   */
  isExpired?: (data: TData) => boolean;
};

export type ExpiryCountdownValue = {
  expiryTime: string;
  isExpired: boolean;
};

/**
 * Cell renderer for expiry countdown. Re-renders every second to keep the countdown
 * ticking. After expiry, displays a grayed-out expiry datetime.
 */
function ExpiryCountdownCellRenderer({ value }: ICellRendererParams<unknown, ExpiryCountdownValue>) {
  const [now, setNow] = useState(() => new Date());

  const expiryDate: Date | undefined = useMemo(() => {
    if (!value?.expiryTime) {
      return undefined;
    }
    try {
      const date = new Date(value.expiryTime);
      if (isNaN(date.getTime())) {
        return undefined; // expiryTime malformed, we got an invalid date
      }

      return date;
    } catch (e) {
      return undefined;
    }
  }, [value?.expiryTime]);

  const isExpiredByTime = expiryDate != null && differenceInMilliseconds(expiryDate, now) <= 0;
  const isExpired = value?.isExpired || isExpiredByTime;
  const remaining = expiryDate != null ? differenceInMilliseconds(expiryDate, now) : undefined;

  useEffect(() => {
    if (expiryDate == null || isExpired) {
      return;
    }

    const interval = setInterval(() => {
      const currentTime = new Date();
      setNow(currentTime);
      if (differenceInMilliseconds(expiryDate, currentTime) <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryDate, isExpired]);

  if (expiryDate == null || remaining == null) {
    return undefined;
  }

  if (isExpired) {
    return <Text color="colorTextMuted">{formattedDate(value?.expiryTime)}</Text>;
  }

  // Create a date at epoch + remaining ms, format as UTC time to get the countdown display
  return <span>{formattedUTCDate(new Date(remaining), '{HH}:{mm}:{ss}')}</span>;
}

/** Countdown column showing time remaining until expiry. After expiry, shows grayed-out expiry datetime. */
export const expiryCountdown: ColDefFactory<Column<ExpiryCountdownParams>> = column => ({
  ...baseColumn(column),
  initialWidth: 120,
  minWidth: 80,
  valueGetter: ({ data, colDef }: ValueGetterParams<unknown>): ExpiryCountdownValue | undefined => {
    if (!colDef.field || !data) {
      return undefined;
    }

    const expiryTime: string | undefined = get(data, colDef.field);
    if (!expiryTime) {
      return undefined;
    }

    const isExpired = column.params?.isExpired?.(data) ?? false;

    return { expiryTime, isExpired };
  },
  cellRenderer: ExpiryCountdownCellRenderer,
  tooltipValueGetter: ({ value }: ITooltipParams<unknown, ExpiryCountdownValue>) => {
    if (!value?.expiryTime) {
      return '';
    }
    return formattedDate(value.expiryTime);
  },
  valueFormatter: ({ value }: ValueFormatterParams<unknown, ExpiryCountdownValue>) => {
    if (!value?.expiryTime) {
      return '';
    }
    return formattedDate(value.expiryTime);
  },
  comparator: (a: ExpiryCountdownValue | undefined, b: ExpiryCountdownValue | undefined) =>
    dateStringComparator(a?.expiryTime, b?.expiryTime),
});
